// src/features/chat/MentionTagsPanel.tsx
//
// Custom @mention tags for a conversation (e.g. "@backend"). Two frequency
// tiers on one panel:
//   - browse + subscribe/unsubscribe: anyone, weekly-ish (ListMentionTags,
//     ListMyMentionTagSubscriptions, SubscribeToMentionTag,
//     UnsubscribeFromMentionTag)
//   - create/delete a tag: conversation admins only, rare
//     (CreateMentionTag, DeleteMentionTag)

import { useEffect, useState } from 'react';
import { chatApi } from '@core/api';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import type { MentionTag } from '@core/types/chat';

export interface MentionTagsPanelProps {
  conversationId: string;
  /** Current user can manage tags (conversation admin/owner). */
  canManage: boolean;
  onClose: () => void;
}

export function MentionTagsPanel({ conversationId, canManage, onClose }: MentionTagsPanelProps) {
  const { t } = useI18n();
  const [tags, setTags] = useState<MentionTag[]>([]);
  const [subscribedIds, setSubscribedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [tagsRes, subsRes] = await Promise.all([
        chatApi.ListMentionTags(conversationId),
        chatApi.ListMyMentionTagSubscriptions(conversationId),
      ]);
      setTags(tagsRes.mention_tags?.items ?? []);
      const subs = new Set<string>();
      for (const s of subsRes.subscriptions?.items ?? []) {
        const tagId = s.mention_tag?.id;
        if (tagId) subs.add(tagId);
      }
      setSubscribedIds(subs);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const toggleSubscription = async (tag: MentionTag) => {
    const tagId = tag.entity?.id;
    if (!tagId) return;
    setBusyId(tagId);
    try {
      if (subscribedIds.has(tagId)) {
        await chatApi.UnsubscribeFromMentionTag(conversationId, tagId);
        setSubscribedIds(prev => {
          const next = new Set(prev);
          next.delete(tagId);
          return next;
        });
      } else {
        await chatApi.SubscribeToMentionTag(conversationId, tagId, {});
        setSubscribedIds(prev => new Set(prev).add(tagId));
      }
    } catch {
      // silent — the toggle simply won't flip; the row stays in its last state
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    setError(null);
    try {
      await chatApi.CreateMentionTag(conversationId, {
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setNewName('');
      setNewDescription('');
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (tag: MentionTag) => {
    const tagId = tag.entity?.id;
    if (!tagId) return;
    if (!confirm(t('chat.mentionTags.deleteConfirm', { name: tag.name }))) return;
    setBusyId(tagId);
    try {
      await chatApi.DeleteMentionTag(conversationId, tagId);
      setTags(prev => prev.filter(t2 => t2.entity?.id !== tagId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-full w-80 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{t('chat.mentionTags.title')}</h3>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {error && <div className="px-4 pt-2 text-xs text-destructive">{error}</div>}

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : tags.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('chat.mentionTags.empty')}</div>
        ) : (
          <ul className="divide-y divide-border">
            {tags.map(tag => {
              const tagId = tag.entity?.id ?? '';
              const isSubscribed = subscribedIds.has(tagId);
              const isBusy = busyId === tagId;
              return (
                <li key={tagId} className="flex items-start gap-2 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">@{tag.display_name || tag.name}</p>
                    {tag.description && <p className="text-xs text-muted-foreground truncate">{tag.description}</p>}
                    <p className="text-[10px] text-muted-foreground">
                      {t('chat.mentionTags.subscriberCount', { count: tag.subscriber_count ?? 0 })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSubscription(tag)}
                    disabled={isBusy}
                    className={`flex-shrink-0 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                      isSubscribed
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    {isSubscribed ? t('chat.mentionTags.subscribed') : t('chat.mentionTags.subscribe')}
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => handleDelete(tag)}
                      disabled={isBusy}
                      className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                      title={t('common.delete')}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {canManage && (
        <div className="border-t border-border p-3">
          {showCreate ? (
            <div className="space-y-2">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder={t('chat.mentionTags.namePlaceholder')}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
              />
              <input
                type="text"
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                placeholder={t('chat.mentionTags.descriptionPlaceholder')}
                className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
              />
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={handleCreate} isLoading={isCreating} disabled={!newName.trim()}>{t('common.create')}</Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              {t('chat.mentionTags.createTag')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default MentionTagsPanel;
